package com.siriusxm.pia.views.unifiedaggregator

import androidx.compose.runtime.*
import com.siriusxm.cmc.CMCViewer
import com.siriusxm.cmc.error
import com.siriusxm.cmc.info
import com.siriusxm.cmc.utils.CMCImages
import com.siriusxm.cmc.utils.getFloat
import com.siriusxm.cmc.utils.getString
import com.siriusxm.pia.components.ColorizableField
import com.siriusxm.pia.components.box
import com.siriusxm.pia.components.colorListViewer
import com.siriusxm.pia.components.colorValueWithHash
import com.siriusxm.pia.rest.unifiedaggregator.Entity
import unifiedcontent.aggregator.*
import contentingestion.unifiedmodel.*
import com.siriusxm.unifiedcontent.console.aggregator.intakeErrorMessage
import com.siriusxm.unifiedcontent.console.aggregator.rememberCanPublishPartial
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import kotlinx.serialization.json.*
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Img
import org.jetbrains.compose.web.dom.Text
import kotlin.time.Clock
import kotlin.time.Duration.Companion.seconds

/**
 * The color override editor for a channel or team: reference images, then the entity's color fields.
 * Saving publishes the `cmc-colors` partial as the console's own producer (UCA-1152); the aggregator
 * authorizes the user through AVP. The fields are editable only when AVP allows that partial for this
 * entity and the console producer is configured for it. That pre-check hides controls; the aggregator
 * still decides on save.
 */
@Composable
fun entityColors(
    aggregator: AggregatorService, entity: Entity, showEntityImages: Boolean? = true,
    reloadCb: suspend () -> Unit
) {
    val coroutineScope = rememberCoroutineScope()
    val canSave by aggregator.rememberCanPublishPartial(COLORS_PARTIAL_ID, entity)

    var fetchedColorList by remember { mutableStateOf<List<ColorizableField>?>(null) }
    suspend fun saveColors(colors: List<ColorizableField>): Boolean {
        try {
            val partialUpdate = entity.getColorPartialUpdate(aggregator.viewer, colors)
            if (partialUpdate != null) {
                aggregator.clientApi.postPartialContentUpdate(
                    partialUpdate
                )
                fetchedColorList = colors
                aggregator.notifications.info(
                    "Colors saved. " +
                            "If the app doesn't reload, you may need to refresh the page."
                )
                delay(3.seconds)
                reloadCb()
            }
            return true
        } catch (e: CancellationException) {
            throw e
        } catch (t: Throwable) {
            val error = intakeErrorMessage("update colors for this entity", t)
            aggregator.notifications.error(error.message, error.description)
        }
        return false
    }

    LaunchedEffect(entity.id, entity.type, entity.version) {
        fetchedColorList = entity.fetchColorList()
    }

    val colors = fetchedColorList
    if (colors != null) {
        if (showEntityImages == true) {
            // images for reference
            val images = extractImages(entity)

            box("Images for Reference", {
                paddedContent = false
            }) {
                images.forEach {
                    Div({ classes(AggregatorStyles.imageBox) }) {
                        Div({ classes(AggregatorStyles.imageContainer) }) {
                            val url = CMCImages.getImageUrl(
                                aggregator.context.configuration.atlasImageServerBaseUrl,
                                it.image.url
                            )
                            Img(url)
                        }
                        Div({ classes(AggregatorStyles.imageLabel) }) {
                            Text("${it.purpose} ${it.aspectRatio.name.removePrefix("aspect_")}")
                        }
                    }
                }
            }
        }
        colorListViewer(colors, canSave) { colorsToSave ->
            coroutineScope.launch {
                saveColors(colorsToSave)
            }
        }
    }
}

fun Entity.getColorPartialUpdate(viewer: CMCViewer, colors: List<ColorizableField>): PartialUpdate? {
    val partialJson = extractColorsJson(this, colors)
    if (partialJson != null) {
        return buildPartialUpdate(this, viewer.email, partialJson)
    }
    return null
}

/** The partial the color editor publishes. */
private const val COLORS_PARTIAL_ID = "cmc-colors"

val typeToColorFieldsMap = mapOf(
    Pair("channel-linear", listOf("lightBackgroundColor", "darkBackgroundColor")),
    Pair("channel-xtra", listOf("lightBackgroundColor", "darkBackgroundColor")),
    Pair("team", listOf("primary", "secondary", "background"))
)

fun Entity.fetchColorList(): List<ColorizableField>? {
    val colorFields = typeToColorFieldsMap[this.type]
    val colorsElement = this["colors"]?.jsonObject
    val colorList = colorFields?.map {
        extractColorizableField(it, colorsElement)
    } ?: listOf()

    return colorList.ifEmpty {
        null
    }
}

/**
 * Represents details about an image including its purpose, aspect ratio, and the image itself.
 */
private data class ImageDetails(
    val purpose: ImagePurpose,
    val aspectRatio: ImageAspectRatio,
    val image: Image
)

private fun extractImages(entity: Entity): List<ImageDetails> {
    val images = mutableListOf<ImageDetails>()

    val imagesMap = entity.images
    imagesMap.entries.map {
        val purpose = it.key
        val aspectRatioMap = it.value
        aspectRatioMap.entries.map {
            val aspectRadio = it.key

            val possibleImages = mutableListOf<Image>()
            it.value.default?.let { img -> possibleImages.add(img) }
            it.value.auto?.let { img -> possibleImages.add(img) }
            it.value.child?.let { img -> possibleImages.add(img) }

            possibleImages.forEach { img ->
                images.add(ImageDetails(purpose, aspectRadio, Image(img.width, img.height, img.url)))
            }
        }
    }
    return images
}

private fun extractColorsJson(entity: Entity, colors: List<ColorizableField>?): JsonElement? {
    val type = entity.type

    console.log(colors)

    var result: JsonElement? = null
    if (type == "channel-linear" || type == "channel-xtra") {
        val lightBackgroundColor = colors?.find { it.field == "lightBackgroundColor" }
        val darkBackgroundColor = colors?.find { it.field == "darkBackgroundColor" }
        val channelColors = ChannelColors(
            lightBackgroundColor = lightBackgroundColor?.toColor(),
            darkBackgroundColor = darkBackgroundColor?.toColor()
        )
        result = Json.encodeToJsonElement(channelColors)
    } else if (type == "team") {
        // "primary", "secondary", "background"
        val primaryColor = colors?.find { it.field == "primary" }
        val secondaryColor = colors?.find { it.field == "secondary" }
        val backgroundColor = colors?.find { it.field == "background" }
        val teamColors = TeamColors(
            primary = primaryColor?.toColor(),
            secondary = secondaryColor?.toColor(),
            background = backgroundColor?.toColor()
        )
        result = Json.encodeToJsonElement(teamColors)
    }
    return result
}

private fun buildPartialUpdate(entity: Entity, viewerEmail: String?, colorsJson: JsonElement): PartialUpdate {
    val id = entity.id
    val type = entity.type
    val version = entity.version

    val partialUpdate = PartialUpdate(
        type = ContentUpdateType.UPDATE,
        timestamp = Clock.System.now(),
        modification = buildJsonObject {
            put("id", id)
            put("type", type)
            put("version", version)
            put("colors", colorsJson)
        },
        partialId = COLORS_PARTIAL_ID,
        operations = listOf(
            PartialUpdateOperation(
                type = PartialUpdateOperationType.REPLACE,
                sourcePath = "$.colors",
                targetPath = "$.colors"
            )
        ),
        auditContext = viewerEmail?.let {
            AuditContext(viewerEmail)
        }
    )
    return partialUpdate
}

private fun extractColorizableField(label: String, colorsElement: JsonObject?): ColorizableField {
    val color = colorsElement?.get(label)?.let { extractColor(it) }
    return ColorizableField(label, color?.hex, color?.opacity)
}

private fun extractColor(jsonElement: JsonElement): Color? {
    val hex = jsonElement.getString("hex")
    val label = jsonElement.getString("label")
    val opacity = jsonElement.getFloat("opacity")

    return hex?.let { hexValue ->
        Color(
            label = label,
            hex = hexValue,
            opacity = opacity
        )
    }
}

fun ColorizableField.toColor(): Color? {
    return color?.let { c ->
        Color(
            label = null, // we dont need to save the label
            hex = colorValueWithHash(c),
            opacity = opacity
        )
    }
}